import React, { Component } from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import { isConsumerMobileNative } from '#src/services/mobile'
import { WithAudioMediaFileRecorderProps, AudioMediaFileRecorder } from '@deal/chat-firebase'
import {
  InitializeAudioRecordingResponse,
  StopAudioRecordingResponse,
  StartAudioRecordingResponse,
  ifBridgeDefined
} from '#src/services/dealNativeBridge'
import {
  withWebMessageEventEmitter,
  WithWebMessageEventEmitterProps
} from '#src/containers/ReceiveMessageFromNative'
import uuidv4 from 'uuid/v4'

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

function withNativeAudioRecorder<P>(
  WrappedComponent: React.ComponentType<P & WithAudioMediaFileRecorderProps>
): React.ComponentType<P> {
  class WithNativeAudioRecorder extends Component<P & WithWebMessageEventEmitterProps> {
    public static displayName = `WithNativeAudioRecorder(${getDisplayName(WrappedComponent)})`
    public constructor(props: P & WithWebMessageEventEmitterProps) {
      super(props)

      this.initialize = this.initialize.bind(this)
      this.startRecording = this.startRecording.bind(this)
      this.stopRecording = this.stopRecording.bind(this)
      this.close = this.close.bind(this)
    }

    private initialize() {
      if (this.props.eventEmitter) {
        return this.props.eventEmitter
          .requestResponse<InitializeAudioRecordingResponse>(
            {
              type: 'initializeAudioRecordingRequest',
              messageId: uuidv4()
            },
            'initializeAudioRecordingResponse'
          )
          .then(response => {
            if (!response.success) {
              throw 'Audio recorder initialization failed.'
            }
          })
      } else {
        return Promise.reject()
      }
    }

    private startRecording() {
      if (this.props.eventEmitter) {
        return this.props.eventEmitter
          .requestResponse<StartAudioRecordingResponse>(
            {
              type: 'startAudioRecordingRequest',
              messageId: uuidv4()
            },
            'startAudioRecordingResponse'
          )
          .then(response => {
            if (!response.success) {
              throw 'Failed to start audio recording.'
            }
          })
      } else {
        return Promise.reject()
      }
    }

    private stopRecording() {
      if (this.props.eventEmitter) {
        return this.props.eventEmitter
          .requestResponse<StopAudioRecordingResponse>(
            {
              type: 'stopAudioRecordingRequest',
              messageId: uuidv4()
            },
            'stopAudioRecordingResponse'
          )
          .then(response => {
            if (!response.success) {
              throw 'Failed to finish audio recording.'
            }

            return response.file!
          })
      } else {
        return Promise.reject()
      }
    }

    private close() {
      ifBridgeDefined(bridge =>
        bridge.sendToNative({
          type: 'closeAudioRecordingMessage'
        })
      )
    }

    public render() {
      let recorder: AudioMediaFileRecorder | undefined = undefined

      if (isConsumerMobileNative()) {
        recorder = {
          initialize: this.initialize,
          startRecording: this.startRecording,
          stopRecording: this.stopRecording,
          close: this.close
        }
      }

      return <WrappedComponent {...this.props} recorder={recorder} />
    }
  }

  return withWebMessageEventEmitter(
    hoistNonReactStatic<P, WithAudioMediaFileRecorderProps>(
      WithNativeAudioRecorder,
      WrappedComponent
    )
  )
}

export { withNativeAudioRecorder }
